import { Box } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { styled as muiStyled } from '@mui/material/styles';
import React from 'react';

import CircularProgress from '../progress/circular-progress';
import Text from '../text';

// Reusable styled cells/rows matching project-table look (theme-aware for dark mode)
export const StyledTableCell = muiStyled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontSize: '14px',
    fontWeight: 600,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: '16px 12px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
  },
}));

export const StyledTableRow = muiStyled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export interface GenericColumn<T> {
  header: React.ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  render: (row: T, index: number) => React.ReactNode;
}

interface GenericTableProps<T> {
  className?: string;
  data: T[];
  columns: GenericColumn<T>[];
  getRowKey?: (row: T, index: number) => React.Key;
  getRowProps?: (
    row: T,
    index: number
  ) => Partial<React.ComponentProps<typeof StyledTableRow>>;
  tableClassName?: string;
  loading?: boolean;
}

export function GenericTable<T>({
  className,
  data,
  columns,
  getRowKey,
  getRowProps,
  tableClassName,
  loading,
}: GenericTableProps<T>) {
  return (
    <>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Table className={className}>
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <StyledTableCell
                  key={idx}
                  style={{ width: col.width }}
                  align={col.align}
                >
                  {col.header}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, i) => (
              <StyledTableRow
                key={getRowKey?.(row, i) ?? i}
                {...(getRowProps?.(row, i) ?? {})}
              >
                {columns.map((col, cIdx) => (
                  <StyledTableCell key={cIdx} align={col.align}>
                    {col.render(row, i)}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

export default GenericTable;
